import PropTypes from "prop-types";
import {
    RiShoppingBag3Line,
    RiStore2Line,
    RiMapPinLine,
    RiMailLine,
    RiPhoneLine,
    RiPrinterLine,
    RiBankCardLine,
} from "react-icons/ri";
import { TiTick } from "react-icons/ti";

const ProposalOrderConfirmModal = ({
    isOpen,
    onClose,
    onConfirm,
    isSaving = false,
    companyName,
    addressTitle,
    fullAddress,
    addressEmail,
    addressPhone,
    addressMobile,
    addressFax,
    paymentMethodName,
    totalBottles,
    totalPrice,
}) => {
    if (!isOpen) return null;

    const contactNumbers = [addressPhone, addressMobile]
        .map((v) => (v ? String(v).trim() : ""))
        .filter(Boolean);
    const contactsString = contactNumbers.join(", ");

    return (
        <div
            className={`fixed inset-0 z-[70] flex items-center justify-center bg-black/55 backdrop-blur-xs p-3 sm:p-5 overflow-y-auto animate-fadeIn ${
                isSaving ? "cursor-not-allowed" : ""
            }`}
            onClick={() => !isSaving && onClose()}
        >
            <div
                className="bg-white rounded-3xl w-full max-w-[640px] max-h-[90vh] my-auto shadow-[0_24px_70px_rgba(0,0,0,0.28)] border border-[#8B2433]/20 flex flex-col transform transition-all animate-fadeIn overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                <style>{`
                    .premium-scrollbar {
                        scrollbar-width: thin;
                        scrollbar-color: #c1c1c1 #f1f1f1;
                    }
                    .premium-scrollbar::-webkit-scrollbar {
                        width: 6px;
                    }
                    .premium-scrollbar::-webkit-scrollbar-track {
                        background: #f1f1f1;
                        border-radius: 10px;
                    }
                    .premium-scrollbar::-webkit-scrollbar-thumb {
                        background: #c1c1c1;
                        border-radius: 10px;
                    }
                    .premium-scrollbar::-webkit-scrollbar-thumb:hover {
                        background: #a8a8a8;
                    }
                `}</style>

                {/* Solid Wine Luxury Header with Shimmer Accent */}
                <div className="bg-gradient-to-r from-[#8B2433] via-[#7D1B28] to-[#5C101B] px-6 py-4 flex justify-between items-center text-white shrink-0 relative shadow-sm overflow-hidden">
                    {/* Top specular shimmer line */}
                    <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#FFD8DF] to-transparent opacity-75" />

                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-white/25 via-white/15 to-white/5 border border-white/20 backdrop-blur-md flex items-center justify-center text-white shrink-0 shadow-inner">
                            <RiShoppingBag3Line className="w-4.5 h-4.5" />
                        </div>
                        <h3 className="text-lg font-bold poppins-semibold text-white tracking-wide">
                            Conferma Ordine
                        </h3>
                    </div>

                    <button
                        type="button"
                        disabled={isSaving}
                        onClick={!isSaving ? onClose : undefined}
                        style={isSaving ? { cursor: "not-allowed" } : undefined}
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-white/85 transition-all text-2xl font-light leading-none ${
                            isSaving
                                ? "opacity-50 cursor-not-allowed pointer-events-auto"
                                : "hover:text-white hover:bg-white/20 cursor-pointer"
                        }`}
                    >
                        &times;
                    </button>
                </div>

                {/* Body Content - Scrollable */}
                <div className="p-5 sm:p-6 pr-4 sm:pr-5 flex flex-col gap-4 bg-gradient-to-b from-[#FDFBFB] via-[#FAF6F7] to-[#F7EFF1] overflow-y-auto flex-1 min-h-0 premium-scrollbar">
                    {/* Clean Borderless Prompt Text */}
                    <p className="text-xs sm:text-[13px] text-[#4A4A4A] poppins-medium leading-relaxed px-1">
                        Sei sicuro di voler confermare e generare l&apos;ordine definitivo? Verifica i dettagli riassuntivi qui sotto:
                    </p>

                    {/* Structured Luxury Review Cards Grid */}
                    <div className="space-y-3.5">
                        {/* Card 1: Cliente & Destinazione */}
                        <div className="bg-gradient-to-br from-[#FAF3F4] via-[#FDF9FA] to-[#F7EAED] border-[1.5px] border-[#8B2433]/22 rounded-2xl p-4.5 sm:p-5 shadow-[0_6px_20px_rgba(139,36,51,0.06)] space-y-3.5 relative overflow-hidden">
                            {/* Subtle top wine accent line */}
                            <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#8B2433]/30 to-transparent" />

                            {/* Company Name */}
                            <div className="flex items-start gap-3 relative z-10">
                                <div className="w-8 h-8 rounded-xl bg-white/90 border border-[#8B2433]/25 text-[#8B2433] flex items-center justify-center shrink-0 mt-0.5 shadow-xs">
                                    <RiStore2Line className="w-4 h-4" />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <span className="text-[10.5px] font-semibold text-gray-400 uppercase tracking-wider block poppins-medium">
                                        Cliente / Ragione Sociale
                                    </span>
                                    <span className="text-xs sm:text-sm font-bold text-[#2E2E2E] poppins-semibold block break-words mt-0.5">
                                        {companyName || "N/D"}
                                    </span>
                                </div>
                            </div>

                            <div className="h-px bg-gradient-to-r from-transparent via-[#8B2433]/20 to-transparent w-full my-0.5" />

                            {/* Selected Full Address */}
                            <div className="flex items-start gap-3 relative z-10">
                                <div className="w-8 h-8 rounded-xl bg-white/90 border border-[#8B2433]/25 text-[#8B2433] flex items-center justify-center shrink-0 mt-0.5 shadow-xs">
                                    <RiMapPinLine className="w-4 h-4" />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 flex-wrap mb-1.5">
                                        <span className="text-[10.5px] font-semibold text-gray-400 uppercase tracking-wider poppins-medium">
                                            Indirizzo di Destinazione
                                        </span>
                                        {addressTitle && (
                                            <span className="text-[10px] bg-white/90 text-[#8B2433] px-2.5 py-0.5 rounded-full border border-[#8B2433]/30 font-semibold poppins-medium shadow-2xs">
                                                {addressTitle}
                                            </span>
                                        )}
                                    </div>

                                    {/* Address Details Lines */}
                                    <div className="space-y-1.5 text-xs text-[#505050] poppins-regular">
                                        {/* Full address line */}
                                        <div className="font-semibold text-[#2E2E2E] leading-relaxed break-words">
                                            {fullAddress || "N/D"}
                                        </div>

                                        {/* Email */}
                                        {addressEmail && (
                                            <div className="flex items-center gap-2 text-gray-600 text-[11.5px] pt-0.5">
                                                <div className="w-4.5 h-4.5 rounded-md bg-white/90 border border-gray-200/80 flex items-center justify-center shrink-0 text-gray-500 shadow-2xs">
                                                    <RiMailLine className="w-2.5 h-2.5" />
                                                </div>
                                                <span className="font-medium text-gray-600 truncate">
                                                    {addressEmail}
                                                </span>
                                            </div>
                                        )}

                                        {/* Contact numbers: telephone & mobile */}
                                        {contactsString && (
                                            <div className="flex items-center gap-2 text-gray-600 text-[11.5px] pt-0.5">
                                                <div className="w-4.5 h-4.5 rounded-md bg-white/90 border border-gray-200/80 flex items-center justify-center shrink-0 text-gray-500 shadow-2xs">
                                                    <RiPhoneLine className="w-2.5 h-2.5" />
                                                </div>
                                                <span className="font-medium text-gray-600 truncate">
                                                    {contactsString}
                                                </span>
                                            </div>
                                        )}

                                        {/* Fax */}
                                        {addressFax && (
                                            <div className="flex items-center gap-2 text-gray-600 text-[11.5px] pt-0.5">
                                                <div className="w-4.5 h-4.5 rounded-md bg-white/90 border border-gray-200/80 flex items-center justify-center shrink-0 text-gray-500 shadow-2xs">
                                                    <RiPrinterLine className="w-2.5 h-2.5" />
                                                </div>
                                                <span className="font-medium text-gray-600 truncate">
                                                    Fax: {addressFax}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Card 2: Condizione di Pagamento & Totali */}
                        <div className="bg-gradient-to-br from-[#FAF3F4] via-[#FDF9FA] to-[#F7EAED] border-[1.5px] border-[#8B2433]/22 rounded-2xl p-4.5 sm:p-5 shadow-[0_6px_20px_rgba(139,36,51,0.06)] space-y-3.5 relative overflow-hidden">
                            {/* Subtle top wine accent line */}
                            <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#8B2433]/30 to-transparent" />

                            {/* Payment Condition Selected Name */}
                            <div className="flex items-start gap-3 relative z-10">
                                <div className="w-8 h-8 rounded-xl bg-white/90 border border-[#8B2433]/25 text-[#8B2433] flex items-center justify-center shrink-0 mt-0.5 shadow-xs">
                                    <RiBankCardLine className="w-4 h-4" />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <span className="text-[10.5px] font-semibold text-gray-400 uppercase tracking-wider block poppins-medium">
                                        Condizione di Pagamento
                                    </span>
                                    <span className="text-xs sm:text-sm font-bold text-[#8B2433] poppins-semibold block break-words mt-0.5">
                                        {paymentMethodName || "N/D"}
                                    </span>
                                </div>
                            </div>

                            <div className="h-px bg-gradient-to-r from-transparent via-[#8B2433]/20 to-transparent w-full my-0.5" />

                            {/* Riepilogo Ordine Luxury Bar with Pezzi & Totale ordine Chips */}
                            <div className="bg-gradient-to-r from-[#8B2433] via-[#7B1927] to-[#5C101B] text-white rounded-xl p-3.5 sm:p-4 shadow-[0_8px_24px_rgba(139,36,51,0.28)] border border-white/15 flex items-center justify-between gap-3 flex-wrap relative overflow-hidden">
                                {/* Top specular highlight shimmer */}
                                <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent" />

                                <div className="flex items-center gap-2 relative z-10">
                                    <span className="w-2 h-2 rounded-full bg-white animate-pulse shrink-0" />
                                    <span className="text-white/95 text-xs sm:text-[13px] poppins-semibold tracking-wide uppercase">
                                        Riepilogo Ordine
                                    </span>
                                </div>

                                <div className="flex items-center gap-2.5 sm:gap-3 ml-auto flex-wrap relative z-10">
                                    {/* Pezzi Chip - Gradient preserved with crystal clear bold number */}
                                    <div className="inline-flex items-center gap-2 bg-gradient-to-b from-black/25 via-black/30 to-black/40 border border-white/25 px-3.5 sm:px-4 py-1.5 rounded-lg shadow-inner backdrop-blur-xs">
                                        <span className="text-white/85 text-xs sm:text-[13px] poppins-medium whitespace-nowrap">
                                            Pezzi:
                                        </span>
                                        <span className="text-white text-sm sm:text-[15px] font-bold poppins-bold tracking-normal leading-none drop-shadow-xs">
                                            {totalBottles}
                                        </span>
                                    </div>

                                    {/* Totale ordine Chip - Gradient preserved with crystal clear bold amount */}
                                    <div className="inline-flex items-center gap-2.5 bg-gradient-to-b from-white/25 via-white/18 to-white/10 border border-white/30 px-4 sm:px-5 py-1.5 rounded-lg shadow-sm backdrop-blur-xs">
                                        <span className="text-white/90 text-xs sm:text-[13px] poppins-medium whitespace-nowrap">
                                            Totale ordine:
                                        </span>
                                        <span className="text-white text-sm sm:text-[15px] font-bold poppins-bold tracking-tight whitespace-nowrap drop-shadow-xs">
                                            {totalPrice}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer Buttons - Pinned Luxury Bar */}
                <div className="bg-gradient-to-r from-[#FCF9FA] via-white to-[#FAF3F5] px-6 py-4 border-t border-[#8B2433]/15 flex justify-end items-center gap-3 shrink-0 shadow-[0_-4px_20px_rgba(139,36,51,0.03)]">
                    <button
                        type="button"
                        disabled={isSaving}
                        onClick={!isSaving ? onClose : undefined}
                        style={isSaving ? { cursor: "not-allowed" } : undefined}
                        className={`rounded-full px-6 py-2.5 text-xs sm:text-sm font-semibold text-gray-700 bg-white border-[1.5px] border-gray-300 transition-all poppins-medium select-none ${
                            isSaving
                                ? "opacity-50 cursor-not-allowed pointer-events-auto"
                                : "hover:bg-gray-50 hover:border-gray-400 hover:shadow-xs cursor-pointer"
                        }`}
                    >
                        Annulla
                    </button>
                    <button
                        type="button"
                        disabled={isSaving}
                        onClick={!isSaving ? onConfirm : undefined}
                        style={isSaving ? { cursor: "not-allowed" } : undefined}
                        className={`rounded-full px-7 py-2.5 text-xs sm:text-sm font-semibold text-white bg-gradient-to-r from-[#8B2433] via-[#9B2336] to-[#B0263C] shadow-[0_6px_20px_rgba(139,36,51,0.35)] transition-all poppins-medium flex items-center gap-2 select-none ${
                            isSaving
                                ? "opacity-70 cursor-not-allowed pointer-events-auto"
                                : "hover:shadow-[0_8px_26px_rgba(139,36,51,0.45)] hover:brightness-105 active:scale-[0.98] cursor-pointer"
                        }`}
                    >
                        {isSaving ? (
                            <>
                                <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin shrink-0" />
                                <span>Conferma in corso...</span>
                            </>
                        ) : (
                            <>
                                <TiTick className="w-4 h-4 text-white shrink-0" />
                                <span>Sì, Conferma Ordine</span>
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

ProposalOrderConfirmModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onConfirm: PropTypes.func.isRequired,
    isSaving: PropTypes.bool,
    companyName: PropTypes.string,
    addressTitle: PropTypes.string,
    fullAddress: PropTypes.string,
    addressEmail: PropTypes.string,
    addressPhone: PropTypes.string,
    addressMobile: PropTypes.string,
    addressFax: PropTypes.string,
    paymentMethodName: PropTypes.string,
    totalBottles: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    totalPrice: PropTypes.string,
};

export default ProposalOrderConfirmModal;
